import React from 'react';
import {
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Badge,
  Box,
  Chip,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { MoreVertical, Trash2 } from 'lucide-react';
import { Conversation } from '../../types';
import { users } from '../../data/sampleData';
import { cases } from '../../data/sampleData';
import { useMessageStore } from '../../store/messageStore';

interface ConversationListProps {
  conversations: Conversation[];
  activeConversation: string | null;
  onConversationSelect: (conversationId: string) => void;
}

export const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  activeConversation,
  onConversationSelect,
}) => {
  const { deleteConversation } = useMessageStore();
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedConversation, setSelectedConversation] = React.useState<string | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, conversationId: string) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedConversation(conversationId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedConversation(null);
  };

  const handleDelete = () => {
    if (selectedConversation) {
      deleteConversation(selectedConversation);
      handleMenuClose();
    }
  };

  const getParticipantName = (participantId: string) => {
    const user = users.find(u => u.id === participantId);
    return user ? user.name : 'Unknown User';
  };

  const getCurrentUser = () => users[0];

  const getOtherParticipant = (conversation: Conversation) => {
    const currentUser = getCurrentUser();
    const otherParticipantId = conversation.participants.find(id => id !== currentUser.id);
    return users.find(u => u.id === otherParticipantId);
  };

  const getCaseDetails = (caseId?: string) => {
    if (!caseId) return null;
    return cases.find(c => c.id === caseId);
  };

  return (
    <Paper 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Conversations
        </Typography>
      </Box>

      <List 
        sx={{ 
          overflow: 'auto',
          flex: 1,
          maxHeight: { xs: 320, md: 'none' },
        }}
      >
        {conversations.map((conversation) => {
          const otherParticipant = getOtherParticipant(conversation);
          const caseDetails = getCaseDetails(conversation.caseId);
          const lastMessage = conversation.lastMessage;

          return (
            <ListItem
              key={conversation.id}
              button
              selected={conversation.id === activeConversation}
              onClick={() => onConversationSelect(conversation.id)}
              sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:last-child': {
                  borderBottom: 'none',
                },
              }}
              secondaryAction={
                <IconButton 
                  edge="end" 
                  onClick={(e) => handleMenuOpen(e, conversation.id)}
                >
                  <MoreVertical size={18} />
                </IconButton>
              }
            >
              <ListItemAvatar>
                <Badge
                  color="error"
                  variant="dot"
                  invisible={conversation.unreadCount === 0}
                  overlap="circular"
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                >
                  <Avatar 
                    src={otherParticipant?.avatar}
                    alt={otherParticipant?.name}
                  />
                </Badge>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <Typography 
                      component="span"
                      sx={{ 
                        fontWeight: conversation.unreadCount > 0 ? 600 : 400,
                      }}
                    >
                      {otherParticipant?.name}
                    </Typography>
                    {lastMessage && (
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        {formatDistanceToNow(new Date(lastMessage.timestamp), { addSuffix: true })}
                      </Typography>
                    )}
                  </Box>
                }
                secondary={
                  <Box>
                    {caseDetails && (
                      <Chip
                        label={caseDetails.caseNumber}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ mb: 0.5 }}
                      />
                    )}
                    {lastMessage && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          fontWeight: conversation.unreadCount > 0 ? 500 : 400,
                        }}
                      >
                        {lastMessage.content}
                      </Typography>
                    )}
                  </Box>
                }
                secondaryTypographyProps={{
                  component: 'div',
                }}
              />
            </ListItem>
          );
        })}
      </List>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <Trash2 size={18} className="text-red-500" />
          </ListItemIcon>
          <ListItemText 
            primary="Delete Conversation"
            primaryTypographyProps={{
              sx: { color: 'error.main' }
            }}
          />
        </MenuItem>
      </Menu>
    </Paper>
  );
};